import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowRight, Tag, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { getBlogPosts, getBlogCategories } from '../services/api';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'all', name: 'All Posts', color: 'bg-[var(--primary)]' },
  { id: 'news', name: 'News', color: 'bg-blue-500' },
  { id: 'tutorials', name: 'Tutorials', color: 'bg-green-500' },
  { id: 'creator-spotlight', name: 'Creator Spotlight', color: 'bg-purple-500' },
  { id: 'announcements', name: 'Announcements', color: 'bg-yellow-500' },
  { id: 'community', name: 'Community', color: 'bg-pink-500' },
];

function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const postsPerPage = 9;

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, currentPage, searchQuery]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await getBlogPosts({
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        search: searchQuery || undefined,
        page: currentPage,
        limit: postsPerPage
      });
      const fetchedPosts = res.data.posts || [];
      // Show demo posts if no posts exist and no search/filter applied
      if (fetchedPosts.length === 0 && selectedCategory === 'all' && !searchQuery) {
        setPosts(getDemoBlogs());
        setTotalPages(1);
      } else {
        setPosts(fetchedPosts);
        setTotalPages(res.data.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      // Show placeholder posts for demo
      setPosts(getDemoBlogs());
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const getDemoBlogs = () => [
    {
      id: '1',
      title: 'Welcome to Supremics: Your Ultimate Web Comic Destination',
      excerpt: 'Discover the world of web comics like never before. Learn about our platform features, creator tools, and how to get started.',
      cover_image: 'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=800',
      category: 'announcements',
      author: 'Supremics Team',
      created_at: new Date().toISOString(),
      read_time: 5,
      tags: ['welcome', 'guide', 'getting-started']
    },
    {
      id: '2',
      title: 'Creator Spotlight: MangaMaster\'s Journey to 1 Million Views',
      excerpt: 'An exclusive interview with one of our top creators about their creative process, tips for new artists, and future projects.',
      cover_image: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=800',
      category: 'creator-spotlight',
      author: 'Editorial Team',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      read_time: 8,
      tags: ['interview', 'creator', 'tips']
    },
    {
      id: '3',
      title: 'How to Use AI Tools to Enhance Your Comics',
      excerpt: 'A comprehensive guide to using our AI-powered colorization, translation, and cover generation tools.',
      cover_image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800',
      category: 'tutorials',
      author: 'Tech Team',
      created_at: new Date(Date.now() - 172800000).toISOString(),
      read_time: 12,
      tags: ['AI', 'tutorial', 'tools']
    },
    {
      id: '4',
      title: 'Weekly Prize Winners: Week 4 Results',
      excerpt: 'Congratulations to this week\'s winners! See the top performing comics and creators.',
      cover_image: 'https://images.unsplash.com/photo-1533227268428-f9ed0900fb3b?w=800',
      category: 'news',
      author: 'Community Team',
      created_at: new Date(Date.now() - 259200000).toISOString(),
      read_time: 3,
      tags: ['prizes', 'winners', 'weekly']
    },
    {
      id: '5',
      title: 'Community Guidelines Update: Building a Positive Space',
      excerpt: 'Important updates to our community guidelines to ensure a safe and welcoming environment for all.',
      cover_image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800',
      category: 'community',
      author: 'Moderation Team',
      created_at: new Date(Date.now() - 345600000).toISOString(),
      read_time: 6,
      tags: ['guidelines', 'community', 'update']
    },
    {
      id: '6',
      title: 'New Feature: Advanced Analytics Dashboard for Creators',
      excerpt: 'Track your comic performance with our new analytics dashboard. Understand your audience better.',
      cover_image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
      category: 'announcements',
      author: 'Product Team',
      created_at: new Date(Date.now() - 432000000).toISOString(),
      read_time: 4,
      tags: ['feature', 'analytics', 'creators']
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat?.color || 'bg-gray-500';
  };

  const filteredPosts = posts.filter(post => {
    if (searchQuery) {
      return post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  return (
    <div className="min-h-screen pb-24" data-testid="blog-page">
      {/* Hero Section */}
      <section className="relative bg-[#0a0a0a] py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E63946]/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <h1 className="text-5xl md:text-6xl font-teko text-white mb-4">
            SUPREMICS <span className="text-[var(--primary)]">BLOG</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            Stay updated with the latest news, creator spotlights, tutorials, and community updates.
          </p>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="sticky top-0 z-30 bg-[var(--background)] border-b border-[var(--border)] py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all
                    ${selectedCategory === cat.id 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--surface)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/20'}`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg text-[var(--text)] focus:outline-none focus:border-[var(--primary)]"
                data-testid="blog-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`blog-skeleton-${i}`} className="bg-[var(--surface)] rounded-xl overflow-hidden animate-pulse">
                <div className="h-48 bg-[var(--border)]" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-20 bg-[var(--border)] rounded" />
                  <div className="h-6 w-3/4 bg-[var(--border)] rounded" />
                  <div className="h-4 w-full bg-[var(--border)] rounded" />
                  <div className="h-4 w-2/3 bg-[var(--border)] rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-2xl font-teko text-[var(--text-secondary)]">No posts found</p>
            <p className="text-[var(--text-secondary)] mt-2">Try a different search or category</p>
          </div>
        ) : (
          <>
            {/* Featured Post (first post) */}
            {currentPage === 1 && filteredPosts[0] && (
              <Link 
                to={`/blog/${filteredPosts[0].id}`}
                className="block mb-12 group"
                data-testid="featured-post"
              >
                <div className="relative h-[400px] rounded-2xl overflow-hidden">
                  <img 
                    src={filteredPosts[0].cover_image}
                    alt={filteredPosts[0].title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <span className={`inline-block px-3 py-1 ${getCategoryColor(filteredPosts[0].category)} text-white text-xs font-medium rounded-full mb-4`}>
                      {CATEGORIES.find(c => c.id === filteredPosts[0].category)?.name || filteredPosts[0].category}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-teko text-white mb-3 group-hover:text-[var(--primary)] transition-colors">
                      {filteredPosts[0].title}
                    </h2>
                    <p className="text-gray-300 mb-4 max-w-2xl">
                      {filteredPosts[0].excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-gray-400 text-sm">
                      <span className="flex items-center gap-1">
                        <User size={14} />
                        {filteredPosts[0].author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(filteredPosts[0].created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {filteredPosts[0].read_time} min read
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Regular Posts Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.slice(currentPage === 1 ? 1 : 0).map(post => (
                <Link 
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-[var(--surface)] rounded-xl overflow-hidden border border-[var(--border)] hover:border-[var(--primary)]/50 transition-all hover:-translate-y-1"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <span className={`absolute top-3 left-3 px-2 py-1 ${getCategoryColor(post.category)} text-white text-xs font-medium rounded-full`}>
                      {CATEGORIES.find(c => c.id === post.category)?.name || post.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-teko text-xl text-[var(--text)] group-hover:text-[var(--primary)] transition-colors line-clamp-2 mb-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {post.read_time} min
                      </span>
                    </div>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {post.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="px-2 py-0.5 bg-[var(--background)] text-xs text-[var(--text-secondary)] rounded">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-[var(--surface)] border border-[var(--border)] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--primary)]/20"
                  data-testid="prev-page"
                >
                  <ChevronLeft size={20} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={`page-${i + 1}`}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all
                      ${currentPage === i + 1 
                        ? 'bg-[var(--primary)] text-white' 
                        : 'bg-[var(--surface)] border border-[var(--border)] hover:bg-[var(--primary)]/20'}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-[var(--surface)] border border-[var(--border)] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--primary)]/20"
                  data-testid="next-page"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Newsletter CTA */}
      <section className="bg-gradient-to-r from-[#E63946] to-[#FF6B35] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-teko text-white mb-4">Stay in the Loop</h2>
          <p className="text-white/80 mb-8">
            Get the latest news, tutorials, and creator spotlights delivered to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => {
            e.preventDefault();
            toast.success('Thanks for subscribing!');
          }}>
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/60 focus:outline-none focus:border-white"
              required
              data-testid="newsletter-email"
            />
            <button 
              type="submit"
              className="px-6 py-3 bg-white text-[#E63946] font-bold rounded-lg hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
              data-testid="newsletter-submit"
            >
              Subscribe <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;
